import { supabase } from "@/integrations/supabase/client";
import type { EventoAgenda } from "@/hooks/useAgenda";

/** Resolve o ID do pedido de origem a partir de um evento de renovação na agenda. */
export async function resolverPedidoIdPorEvento(evento: EventoAgenda): Promise<number | null> {
  if (evento.pedido_id) return Number(evento.pedido_id);

  if (evento.titulo) {
    const match = evento.titulo.match(/- (\d+) -/);
    if (match) return parseInt(match[1], 10);
  }

  if (evento.contrato_id) {
    const { data: contrato } = await supabase
      .from("contratos")
      .select("pedido_id, pedido:pedidos(id)")
      .eq("id", evento.contrato_id)
      .single();

    const pedidoId =
      contrato?.pedido_id ??
      (contrato?.pedido as { id?: number } | null)?.id ??
      (Array.isArray(contrato?.pedido) ? contrato.pedido[0]?.id : null);
    if (pedidoId) return Number(pedidoId);
  }

  if (evento.cliente_id) {
    const { data: pedidos } = await supabase
      .from("pedidos")
      .select("id")
      .eq("cliente_id", evento.cliente_id)
      .neq("situacao_renovacao", "nao_renovado")
      .order("created_at", { ascending: false })
      .limit(1);

    if (pedidos?.[0]?.id) return pedidos[0].id;
  }

  return null;
}

export type PedidoOrigemRenovacao = {
  id: number;
  cliente_id: number;
  projeto_id?: number | null;
  orcamento_id?: number | null;
  valor_total?: number | null;
  prazo_entrega?: number | null;
  vendedor_id?: string | null;
  situacao_renovacao?: string | null;
  tipo_negocio?: string | null;
  numero_ciclo?: number | null;
  cliente?: { id: number; nome: string };
  orcamento?: {
    id?: number;
    valor_total?: number;
    valor_seguro?: number;
    quantidade_placas?: number;
    plano_id?: string | null;
    vigencia_anos?: number | null;
    forma_pagamento?: string | null;
    parcelas_cartao?: number | null;
    numero_parcelas?: number | null;
    vendedor_id?: string | null;
  } | null;
};

export async function buscarPedidoOrigemRenovacao(
  pedidoAnteriorId: number
): Promise<PedidoOrigemRenovacao | null> {
  const { data, error } = await supabase
    .from("pedidos")
    .select(
      `
      id,
      cliente_id,
      projeto_id,
      orcamento_id,
      valor_total,
      prazo_entrega,
      vendedor_id,
      situacao_renovacao,
      tipo_negocio,
      numero_ciclo,
      cliente:clientes(id, nome),
      orcamento:orcamentos!orcamento_id(
        id,
        valor_total,
        valor_seguro,
        quantidade_placas,
        plano_id,
        vigencia_anos,
        forma_pagamento,
        parcelas_cartao,
        numero_parcelas,
        vendedor_id
      )
    `
    )
    .eq("id", pedidoAnteriorId)
    .single();

  if (error) throw error;
  return data as unknown as PedidoOrigemRenovacao;
}
